import { useCallback, useEffect, useRef, useState } from 'react';
import { useTheme } from '../context/ThemeContext';
import Chart from '../chart';
import useAutoRefresh from '../hooks/useAutoRefresh';

export default function DowntimeVsOperationalChart() {
  const canvasRef = useRef(null);
  const chartRef = useRef(null);
  const { theme } = useTheme();
  const [perc, setPerc] = useState({ downtime: 0, operational: 100 });

  const fetchLatest = useCallback(async () => {
    try {
      const res = await fetch(`/api/equipment_status.php?latest=1&_=${Date.now()}`);
      const json = await res.json();
      json.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      const latest = json[0];
      if (latest) {
        const parseHours = (start, end) => {
          if (!start || !end) return 0;
          const s = new Date(`1970-01-01T${start}Z`);
          const e = new Date(`1970-01-01T${end}Z`);
          const diff = (e - s) / 3600000;
          return diff > 0 ? diff : 0;
        };
        const planned = parseHours(
          latest.planned_downtime_start,
          latest.planned_downtime_end
        );
        const unplanned = parseHours(
          latest.unplanned_downtime_start,
          latest.unplanned_downtime_end
        );
        const downtimeHours = planned + unplanned;
        const downtimePercentage = (downtimeHours / 24) * 100;
        const operationalPercentage = 100 - downtimePercentage;
        setPerc({
          downtime: downtimePercentage,
          operational: operationalPercentage,
        });
      }
    } catch (e) {
      console.error(e);
    }
  }, []);

  useAutoRefresh(fetchLatest, []);

  useEffect(() => {
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;

    chartRef.current?.destroy();
    chartRef.current = new Chart(ctx, {
      type: 'bar',
      data: {
        labels: ['Downtime', 'Operational Time'],
        datasets: [
          {
            data: [perc.downtime, perc.operational],
            borderRadius: 18,
            borderSkipped: false,
            barThickness: 32,
            backgroundColor: (context) => {
              const { ctx, chartArea } = context.chart;
              if (!chartArea) {
                return context.dataIndex === 0
                  ? 'rgba(239,68,68,0.9)'
                  : 'rgba(34,197,94,0.9)';
              }
              const gradient = ctx.createLinearGradient(
                chartArea.left,
                0,
                chartArea.right,
                0
              );
              if (context.dataIndex === 0) {
                gradient.addColorStop(0, 'rgba(248,113,113,0.95)');
                gradient.addColorStop(1, 'rgba(244,63,94,0.9)');
              } else {
                gradient.addColorStop(0, 'rgba(56,189,248,0.95)');
                gradient.addColorStop(1, 'rgba(16,185,129,0.95)');
              }
              return gradient;
            },
          },
        ],
      },
      options: {
        indexAxis: 'y',
        responsive: true,
        maintainAspectRatio: false,
        animation: { duration: 600, easing: 'easeOutQuart' },
        plugins: {
          legend: { display: false },
          datalabels: {
            anchor: 'end',
            align: 'end',
            offset: 6,
            font: { weight: 600 },
            color: theme === 'dark' ? '#f8fafc' : '#0f172a',
            formatter: (value) => `${value.toFixed(1)}%`,
          },
          tooltip: {
            backgroundColor: 'rgba(15,23,42,0.9)',
            titleColor: '#e2e8f0',
            bodyColor: '#f1f5f9',
            borderColor: 'rgba(148,163,184,0.2)',
            borderWidth: 1,
            callbacks: {
              label: (context) => `${context.label}: ${context.parsed.x.toFixed(1)}%`,
            },
          },
        },
        scales: {
          x: {
            beginAtZero: true,
            max: 100,
            ticks: {
              color: theme === 'dark' ? '#cbd5f5' : '#1f2937',
              callback: (value) => `${value}%`,
            },
            grid: {
              color: theme === 'dark' ? 'rgba(148,163,184,0.15)' : 'rgba(148,163,184,0.2)',
              drawBorder: false,
            },
          },
          y: {
            ticks: {
              color: theme === 'dark' ? '#e2e8f0' : '#1f2937',
              font: { weight: 600 },
            },
            grid: { display: false },
          },
        },
      },
    });

    return () => {
      chartRef.current?.destroy();
      chartRef.current = null;
    };
  }, [perc, theme]);

  return (
    <div className="relative w-full h-full">
      <canvas
        ref={canvasRef}
        className="absolute inset-0 w-full h-full"
      />
    </div>
  );
}

